import type { TFunction } from 'i18next';
import { UserErrorResponseCodeEnum } from '../api/generated';
import { getApiErrorCode, getApiErrorMessage } from '../utils/errorMessageUtils';

/**
 * The sentence shown for each way the backend refuses a user write.
 *
 * The backend sends a `UserErrorCode` precisely so this side owns the wording:
 * its own message is English and stays English, so showing it would have put
 * untranslated text in a Danish UI. Typed as a total Record, so a new code in
 * the generated client fails the build here rather than reaching an operator as
 * a generic "could not save".
 */
const USER_ERROR_MESSAGE_KEYS: Record<UserErrorResponseCodeEnum, string> = {
  [UserErrorResponseCodeEnum.RoleAssignmentRequired]: 'config-user-error-role-required',
  [UserErrorResponseCodeEnum.DuplicateRoleAssignment]: 'config-user-error-duplicate-location',
  [UserErrorResponseCodeEnum.SelfDeleteForbidden]: 'config-user-error-self-delete',
  [UserErrorResponseCodeEnum.LastAccountAdminDelete]: 'config-user-error-last-admin-delete',
  [UserErrorResponseCodeEnum.LastAccountAdminDemotion]: 'config-user-error-last-admin-demotion',
  [UserErrorResponseCodeEnum.DuplicateEmail]: 'config-user-error-duplicate-email',
  [UserErrorResponseCodeEnum.UserNotFound]: 'config-user-error-not-found'
};

const isUserErrorCode = (code: string): code is UserErrorResponseCodeEnum =>
  code in USER_ERROR_MESSAGE_KEYS;

/**
 * Turns a rejected user write into the sentence shown to the operator.
 *
 * A code we do not recognise is treated as no code at all: an older frontend
 * against a newer backend must not print a raw enum name. The fallback stays
 * the caller's, because "could not create" and "could not delete" are not the
 * same sentence.
 */
export const buildUserErrorMessage = (error: unknown, t: TFunction, fallback: string): string => {
  const code = getApiErrorCode(error);
  if (code !== undefined && isUserErrorCode(code)) {
    return t(USER_ERROR_MESSAGE_KEYS[code]);
  }
  // No code: a 403 from the permission layer, a network failure, or an endpoint
  // that still answers with a plain string.
  return getApiErrorMessage(error, fallback);
};
